import type {
  AssetTypeResponseDto,
  EventStateResponseDto,
  ParameterNameResponseDto,
  GetAllAssetTypeSubsystemComponentClassComponentTypesResponseDto,
  ComponentClassResponseDto,
  GetAssetTypeSubsystemComponentclassResponseDtoShort,
} from '@evimo/client';
import type {
  AssetModelResponseDto,
  AssetTypeManufacturerModelResponseDto,
  ManufacturerResponseDto,
} from '@evimo/types';
import type { LookupRow } from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import type {
  EnrichedSubsystem,
  ComponentTypeResponseDto,
} from '~/app/services/knowledgeBase/types';

export function mapAssetType(
  dto: AssetTypeResponseDto & { subsystemIds?: string[] },
): LookupRow {
  const subsystemIds = Array.isArray(dto.subsystemIds) ? dto.subsystemIds : [];
  return {
    _key: dto.id,
    code: dto.code,
    name: dto.name,
    description: dto.description ?? '',
    metadataSchema: dto.metadataSchema
      ? JSON.stringify(dto.metadataSchema)
      : '',
    subsystemIds,
    associatedSubsystemsCount: dto.subsystemCount ?? subsystemIds.length,
  };
}

export function mapSubsystem(dto: EnrichedSubsystem): LookupRow {
  return {
    _key: dto.id,
    code: dto.code,
    name: dto.name,
    description: dto.description ?? '',
    metadataSchema: dto.metadataSchema
      ? JSON.stringify(dto.metadataSchema)
      : '',
    componentClassIds: dto.componentClassIds || [],
    componentClassCodes: dto.componentClassCodes || [],
    assetTypeIds: dto.assetTypeIds || [],
  };
}

export function mapComponentClassWithLookup(
  dto: ComponentClassResponseDto & {
    componentTypesCount?: number;
    description?: string | null;
    synonyms?: string[];
  },
  idToCodeMap: Map<string, string>,
): LookupRow {
  const parentCode = dto.parentId ? idToCodeMap.get(dto.parentId) : '';
  return {
    _key: dto.id,
    code: String(dto.code),
    name: dto.name,
    description: dto.description ?? '',
    synonyms: dto.synonyms ?? [],
    parentCode: parentCode ? String(parentCode) : '',
  };
}

export function mapComponentTypeWithLookup(
  dto: ComponentTypeResponseDto,
): LookupRow {
  return {
    _key: dto.id,
    name: dto.name,
    status: dto.status ?? '',
    synonyms: dto.synonyms ?? [],
  };
}

export function mapEventState(dto: EventStateResponseDto): LookupRow {
  return {
    _key: dto.id,
    code: String(dto.code),
    name: dto.name,
  };
}

export function mapParameterName(dto: ParameterNameResponseDto): LookupRow {
  return {
    _key: dto.id,
    code: String(dto.code),
    name: dto.name,
    unit: dto.unit ?? '',
  };
}

export function mapComponentType(dto: ComponentTypeResponseDto): LookupRow {
  return {
    _key: dto.id,
    name: dto.name,
    status: dto.status ?? '',
    synonyms: dto.synonyms ?? [],
    associatedComponentClassesCount: dto.componentClassesCount ?? 0,
  };
}

export function mapComponentClass(
  dto: ComponentClassResponseDto & {
    componentTypesCount?: number;
    description?: string | null;
    synonyms?: string[];
    componentTypes?: string[];
  },
): LookupRow {
  const componentTypesCount =
    typeof dto.componentTypesCount === 'number'
      ? dto.componentTypesCount
      : Array.isArray(dto.componentTypes)
        ? dto.componentTypes.length
        : 0;
  return {
    _key: dto.id,
    name: dto.name,
    code: dto.code ?? '',
    description: dto.description ?? '',
    synonyms: dto.synonyms ?? [],
    associatedComponentTypesCount: componentTypesCount,
  };
}

export function mapManufacturer(dto: ManufacturerResponseDto): LookupRow {
  return {
    _key: dto.id,
    name: dto.name,
    status: dto.status ?? '',
    logName: dto.logName ?? '',
  };
}

export function mapAssetModel(
  dto: AssetModelResponseDto,
  assetTypeById: Map<string, { code: string; name: string }>,
): LookupRow {
  const assetType = assetTypeById.get(dto.assetTypeId);
  return {
    _key: dto.id,
    name: dto.name,
    description: dto.description ?? '',
    assetTypeCode: assetType?.code ?? '',
    assetTypeName: assetType?.name ?? '',
  };
}

export function mapAssetTypeManufacturerModel(
  dto: AssetTypeManufacturerModelResponseDto,
  assetTypeCodeById: Map<string, string>,
): LookupRow {
  return {
    _key: dto.id,
    assetTypeCode: assetTypeCodeById.get(dto.assetTypeId) ?? '',
    assetTypeName: dto.assetTypeName,
    manufacturerName: dto.manufacturerName,
    assetModelName: dto.assetModelName,
  };
}

export function mapAssetSubsystemComponentClass(
  dto: GetAssetTypeSubsystemComponentclassResponseDtoShort,
): LookupRow {
  return {
    _key: dto.id,
    assetTypeSubsystemId: dto.assetTypeSubsystemId,
    assetTypeCode: dto.assetTypeCode,
    subsystemCode: dto.subsystemCode,
    associatedComponentClassCount: dto.associatedComponentClassCount,
    code: `${dto.assetTypeCode} - ${dto.subsystemCode}`,
    name: `${dto.assetTypeCode} / ${dto.subsystemCode} Component Class Mapping`,
  };
}

export function mapAssetSubsystemComponentClassComponentType(
  dto: GetAllAssetTypeSubsystemComponentClassComponentTypesResponseDto,
): LookupRow {
  return {
    _key: dto.id,
    assetTypeSubsystemComponentClassId: dto.assetTypeSubsystemComponentClassId,
    assetTypeCode: dto.assetTypeCode,
    subsystemCode: dto.subsystemCode,
    componentClassCode: dto.componentClassCode,
    associatedComponentTypesCount: dto.associatedComponentTypesCount,
    // Convenience display fields
    code: `${dto.assetTypeCode} - ${dto.subsystemCode} - ${dto.componentClassCode}`,
    name: `${dto.assetTypeCode} / ${dto.subsystemCode} / ${dto.componentClassCode}`,
  };
}
